import type {
  Tunnus,
  TunnusBroadcastMessageVariant,
  UserInfo,
} from '@yleisradio/tunnus-sdk';
import { useAnalyticsAvailability } from 'contexts/AnalyticsAvailabilityContext';
import { useUILanguage } from 'hooks/useUILanguage';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import logger from 'services/logger';
import { isTestEnvironment } from 'utils/environment';
import { IdTokenRefresher } from './IdTokenRefresher';

const tunnusSdkPromise = import('@yleisradio/tunnus-sdk');

type Value = {
  isLoggedIn: boolean;
  isUserInformationFetched: boolean;
  user: UserInfo | undefined;
  yleTunnusInstance: Tunnus | undefined;
};

const TunnusContext = createContext<Value>({
  isLoggedIn: false,
  isUserInformationFetched: false,
  user: undefined,
  yleTunnusInstance: undefined,
});

type Props = {
  children: React.ReactNode;
  hasTunnusSessionCookie: boolean;
};

export const TunnusContextProvider: React.FunctionComponent<Props> = ({
  children,
  hasTunnusSessionCookie,
}) => {
  const [yleTunnusInstance, setYleTunnusInstance] = useState<Tunnus>();
  const [isUserInformationFetched, setIsUserInformationFetched] =
    useState<boolean>(false);
  const [userInformation, setUserInformation] = useState<UserInfo>();
  const isLoggedIn = isUserInformationFetched
    ? !!userInformation
    : hasTunnusSessionCookie;
  const isSessionValidated = isLoggedIn && isUserInformationFetched;

  const language = useUILanguage();
  const { isAvailable: isAnalyticsAvailable, hasErrored: hasAnalyticsErrored } =
    useAnalyticsAvailability();
  const router = useRouter();

  useEffect(() => {
    if (isAnalyticsAvailable || hasAnalyticsErrored) {
      (async function () {
        try {
          const { default: createTunnus } = await tunnusSdkPromise;

          const tunnus = createTunnus({
            trackingAppName: 'areena-web-items',
            initiatingApp: 'areena-web-items',
            language: language,
            returnToUrl: window.location.href,
            environment: isTestEnvironment() ? 'test' : 'production',
            theme: 'dark',
            openingEvent: 'defaultWithFocus',
          });

          setYleTunnusInstance(tunnus);

          const user = await tunnus.getUserInfo();
          setIsUserInformationFetched(true);
          if (user) {
            setUserInformation(user);
          }
        } catch (e) {
          logger.error(e, 'Error initializing Yle Tunnus SDK');
        }
      })();
    }
  }, [hasAnalyticsErrored, isAnalyticsAvailable, language]);

  useEffect(() => {
    const tunnusChangesChannel = new BroadcastChannel('yle-tunnus');

    const listener = ({
      data: message,
    }: MessageEvent<TunnusBroadcastMessageVariant>) => {
      if (message.type === 'tunnus-user-info-changed') {
        setIsUserInformationFetched(true);
        setUserInformation(message.user);
      } else if (message.type === 'tunnus-logged-in') {
        setIsUserInformationFetched(true);
        setUserInformation(message.user);

        // Refetch view data for the logged-in user
        router.replace(router.asPath, undefined, { scroll: false });
      } else if (message.type === 'tunnus-logged-out') {
        setUserInformation(undefined);

        // Refetch view data for the logged-out user
        router.replace(router.asPath, undefined, { scroll: false });
      }
    };

    tunnusChangesChannel.addEventListener('message', listener);

    return () => {
      tunnusChangesChannel.removeEventListener('message', listener);
      tunnusChangesChannel.close();
    };
  }, [router]);

  const value = useMemo(
    (): Value => ({
      isLoggedIn,
      isUserInformationFetched,
      user: userInformation,
      yleTunnusInstance,
    }),
    [isLoggedIn, isUserInformationFetched, userInformation, yleTunnusInstance]
  );

  return (
    <TunnusContext.Provider value={value}>
      {!!yleTunnusInstance && isSessionValidated && (
        <IdTokenRefresher yleTunnusInstance={yleTunnusInstance} />
      )}

      {children}
    </TunnusContext.Provider>
  );
};

export function useTunnusContext(): Value {
  return useContext(TunnusContext);
}
